/**
 * Project Setup Page - Frontend Workflow Implementation
 * All-in-one setup page with consolidated inputs: Dates, Tax Values, Millage, DRF
 * Part of Frontend Workflow - Issue 4 (SPJ-139)
 */

import { Button, VStack } from '@chakra-ui/react';
import { ArrowLeft } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { BondInterestRateInputs } from '~/builders/bond-interest-rates/BondInterestRateInputs';
import { DRFInfoInputs } from '~/builders/drf-info/DRFInfoInputs';
import { MillageInfoInputs } from '~/builders/millage-info/MillageInfoInputs';
import { DateInputs } from '~/builders/project-dates/ProjectDateInputs';
import { TaxableValueInputs } from '~/builders/taxable-value/TaxableValueInputs';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { ErrorFallback } from '~/components/ErrorBoundary/ErrorFallback';
import { PageLayoutGrid } from '~/components/layout/page-utilities/PageLayoutGrid';
import { WorkflowStatusSidebar } from '~/components/layout/page-utilities/WorkflowStatusSidebar';
import { useAppHeader } from '~/components/layout/shared/AppHeaderContext';
import { SidebarProvider } from '~/components/layout/shared/SidebarContext';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { SectionCard } from '~/components/project';
import { useAppSettingsLoader } from '~/hooks/useAppSettings';
import { useAutoExpandSections } from '~/hooks/useAutoExpandSections';
import { useProjectSetupHandlers } from '~/hooks/useProjectSetupHandlers';
import { type ProjectStore, useProjectStore } from '~/hooks/useProjectStore';
import { useQuickFill } from '~/hooks/useQuickFill';
import { useWorkflowStatus } from '~/hooks/useWorkflowStatus';
import { PROJECT_ROUTES } from '~/pages/project/projectRoutes';
import type { BondInterestRateSettings } from '~/types/app-settings';

const ProjectSetupPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setHeader } = useAppHeader();

  // Note: Project loading is handled by ProjectRoute wrapper
  // By the time this page renders, project is guaranteed to exist

  // Track whether to show validation errors (set to true when user clicks Continue)
  const [showValidation, setShowValidation] = useState(false);

  // Track bond interest rates section expansion separately (not part of workflow status)
  const [bondInterestRatesExpanded, setBondInterestRatesExpanded] = useState(false);

  // Get app settings for bond interest rate defaults (loads from database)
  const { settings: appSettings } = useAppSettingsLoader();

  // Store selectors (don't call functions that modify state in selectors!)
  // Memoize selector function to prevent Zustand getSnapshot warning
  const projectSelector = useCallback(
    (s: ProjectStore) => (projectId ? s.projects[projectId] : undefined),
    [projectId]
  );
  const project = useProjectStore(projectSelector);

  // Get existing data - combine selectors with shallow comparison to prevent extra renders
  // Zustand v5: useShallow wraps an INLINE selector (not a memoized callback)
  const { dateInputs, taxValueInputs, millageInputs, drfInputs } = useProjectStore(
    useShallow((s) => ({
      dateInputs: projectId ? s.projects[projectId]?.dateInputs : undefined,
      taxValueInputs: projectId ? s.projects[projectId]?.taxValueInputs : undefined,
      millageInputs: projectId ? s.projects[projectId]?.millageInputs : undefined,
      drfInputs: projectId ? s.projects[projectId]?.drfInputs : undefined,
    }))
  );

  // Get workflow statuses from hook (single source of truth)
  const sectionStatuses = useWorkflowStatus(projectId || '');

  // Track which sections are expanded - use hook to manage auto-expansion
  const { expandedSections, setExpandedSections } = useAutoExpandSections(sectionStatuses, {
    dates: true, // Dates section starts expanded
    tax: false,
    millage: false,
    drf: false,
  });

  // Get handlers from hook
  const handlers = useProjectSetupHandlers(projectId || '');

  // Get quick-fill actions from hook
  const _quickFill = useQuickFill(projectId || '');

  // Initialize bond interest rates from project or app baseline
  const [bondInterestRates, setBondInterestRates] = useState<BondInterestRateSettings>(
    project?.bondInterestRateSettings || appSettings.bondInterestRates
  );

  // Extract primitive values to use as dependencies (prevents infinite loops)
  // DO NOT use sectionStatuses object directly in dependencies!
  const datesStatus = sectionStatuses.dates;
  const taxStatus = sectionStatuses.tax;
  const millageStatus = sectionStatuses.millage;
  const drfStatus = sectionStatuses.drf;

  // Derive completion status from section statuses
  // CRITICAL: Depend on primitive values, NOT the sectionStatuses object
  const completionStatus = useMemo(() => {
    const datesComplete = datesStatus === 'complete';
    const taxComplete = taxStatus === 'complete';
    const millageComplete = millageStatus === 'complete';
    const drfComplete = drfStatus === 'complete';

    return {
      dates: datesComplete,
      tax: taxComplete,
      millage: millageComplete,
      drf: drfComplete,
      allComplete: datesComplete && taxComplete && millageComplete && drfComplete,
      // Use section statuses directly from hook
      datesStatus,
      taxStatus,
      millageStatus,
      drfStatus,
    };
  }, [datesStatus, taxStatus, millageStatus, drfStatus]);

  // Determine gating for sections - must be before hooks to ensure they're always defined
  const isDatesEnabled = true;
  const isTaxEnabled = completionStatus.dates;
  const isMillageEnabled = completionStatus.dates && completionStatus.tax;
  const isDRFEnabled = completionStatus.dates && completionStatus.tax && completionStatus.millage;
  const isBondInterestRatesEnabled =
    completionStatus.dates &&
    completionStatus.tax &&
    completionStatus.millage &&
    completionStatus.drf;

  // Navigation handlers - wrapped in useCallback to prevent infinite loops
  // MUST be before early return to satisfy React hooks rules
  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleAddExistingDebt = useCallback(() => {
    if (!projectId) return;

    // Require DRF section to be complete before navigating (DRF requires dates+tax+millage)
    if (!completionStatus.drf) {
      console.warn('[ProjectSetup] Cannot navigate to existing debt - DRF section not complete');
      setShowValidation(true);
      return;
    }

    // Save bond interest rate settings
    const saveBondInterestRateSettings = useProjectStore.getState().saveBondInterestRateSettings;
    saveBondInterestRateSettings(projectId, bondInterestRates);

    // Build all calculated data before navigating
    const results = handlers.handleSaveAndContinue();

    if (!results.dates) {
      console.error('[ProjectSetup] Failed to build dates before navigating to existing debt');
      // Could show error toast here
      return;
    }

    // Navigate to existing debt page
    navigate(`/projects/${projectId}/${PROJECT_ROUTES.EXISTING_DEBT}`);
  }, [completionStatus.drf, handlers, navigate, projectId, bondInterestRates]);

  const handleProposalBuilder = useCallback(() => {
    if (!projectId) return;
    // If not all sections are complete, show validation errors and don't navigate
    if (!completionStatus.allComplete) {
      setShowValidation(true);
      return;
    }

    // Save bond interest rate settings
    const saveBondInterestRateSettings = useProjectStore.getState().saveBondInterestRateSettings;
    saveBondInterestRateSettings(projectId, bondInterestRates);

    // Build all calculated data before navigating
    const results = handlers.handleSaveAndContinue();

    if (!results.dates) {
      console.error('[ProjectSetup] Failed to build dates');
      // Could show error toast here
      return;
    }

    // All sections complete and built, navigate to proposal page
    setShowValidation(false); // Reset validation state
    navigate(`/projects/${projectId}/${PROJECT_ROUTES.PROPOSAL}`);
  }, [completionStatus.allComplete, projectId, bondInterestRates, handlers, navigate]);

  // Extract primitive values for header config dependencies
  // CRITICAL: Extract primitives BEFORE using in useEffect to prevent infinite loops
  const drfComplete = completionStatus.drf;
  const allComplete = completionStatus.allComplete;

  // Use ref to track previous values and prevent unnecessary updates
  const prevValuesRef = useRef<{ drfComplete?: boolean; allComplete?: boolean }>({});

  // Set header configuration - only update when primitive values actually change
  useEffect(() => {
    // Skip if values haven't actually changed (but always run on first render)
    if (
      prevValuesRef.current.drfComplete === drfComplete &&
      prevValuesRef.current.allComplete === allComplete &&
      prevValuesRef.current.drfComplete !== undefined
    ) {
      return;
    }

    prevValuesRef.current = { drfComplete, allComplete };

    setHeader({
      title: 'Project Setup',
      leftActions: (
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to Home
        </Button>
      ),
      rightActions: (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddExistingDebt}
            disabled={!drfComplete}
          >
            Existing Debt
          </Button>
          <Button
            variant="solid"
            colorPalette="blue"
            size="sm"
            onClick={handleProposalBuilder}
            disabled={!allComplete}
          >
            Proposal Builder
          </Button>
        </>
      ),
    });
  }, [
    setHeader,
    handleBack,
    handleAddExistingDebt,
    handleProposalBuilder,
    drfComplete,
    allComplete,
  ]);

  // Note: Loading and not-found states are handled by ProjectRoute wrapper
  // Project is guaranteed to exist at this point

  return (
    <SidebarProvider>
      <PageLayoutGrid rightSidebar={<WorkflowStatusSidebar projectId={projectId || ''} />}>
        <VStack
          maxW={LAYOUT_SPACING.MAX_WIDTH_CONTENT}
          mx="auto"
          p={LAYOUT_SPACING.PAGE_PADDING}
          gap={LAYOUT_SPACING.SECTION_GAP}
          align="stretch"
        >
          {/* Date Inputs Section */}
          <SectionCard
            title="Project Dates"
            description="Configure fiscal year, tax collection schedule, and payment dates"
            status={completionStatus.datesStatus}
            isRequired
            isDisabled={!isDatesEnabled}
            showValidation={showValidation}
            isExpanded={expandedSections.dates}
            onExpandedChange={(expanded) =>
              setExpandedSections((prev) => ({ ...prev, dates: expanded }))
            }
          >
            <ErrorBoundary fallback={<ErrorFallback section="dates" />}>
              <DateInputs
                values={
                  dateInputs || {
                    startingTaxYear: '',
                    fiscalYearEnd: '',
                    taxCollectionSchedule: undefined,
                    paymentDates: [],
                  }
                }
                onUpdate={handlers.handleDateInputsChange}
                showValidation={showValidation}
                isDisabled={!isDatesEnabled}
              />
            </ErrorBoundary>
          </SectionCard>

          {/* Tax Value Inputs Section */}
          <SectionCard
            title="Taxable Value Projections"
            description="Configure initial taxable value and growth rate projections"
            status={completionStatus.taxStatus}
            isRequired
            isDisabled={!isTaxEnabled}
            showValidation={showValidation}
            isExpanded={expandedSections.tax}
            onExpandedChange={(expanded) =>
              setExpandedSections((prev) => ({ ...prev, tax: expanded }))
            }
          >
            <ErrorBoundary fallback={<ErrorFallback section="taxable value projections" />}>
              <TaxableValueInputs
                values={
                  taxValueInputs || {
                    initialTaxableValue: '',
                    startYearGrowthRate: '',
                    fiveYearGrowthRate: '',
                    twentyYearGrowthRate: '',
                    startingTaxYear: '',
                  }
                }
                onUpdate={handlers.handleTaxValueInputsChange}
                showValidation={showValidation}
                isDisabled={!isTaxEnabled}
              />
            </ErrorBoundary>
          </SectionCard>

          {/* Millage Inputs Section */}
          <SectionCard
            title="Millage Configuration"
            description="Configure peak and minimum millage rates and durations"
            status={completionStatus.millageStatus}
            isRequired
            isDisabled={!isMillageEnabled}
            showValidation={showValidation}
            isExpanded={expandedSections.millage}
            onExpandedChange={(expanded) =>
              setExpandedSections((prev) => ({ ...prev, millage: expanded }))
            }
          >
            <ErrorBoundary fallback={<ErrorFallback section="millage configuration" />}>
              <MillageInfoInputs
                values={
                  millageInputs || {
                    peakMillage: '',
                    peakMillageDuration: '1',
                    minimumMillage: '',
                    minimumMillageDuration: '1',
                  }
                }
                onUpdate={handlers.handleMillageInputsChange}
                showValidation={showValidation}
                isDisabled={!isMillageEnabled}
              />
            </ErrorBoundary>
          </SectionCard>

          {/* DRF Inputs Section */}
          <SectionCard
            title="Debt Reserve Fund (DRF)"
            description="Configure debt reserve fund balance constraints"
            status={completionStatus.drfStatus}
            isRequired
            isDisabled={!isDRFEnabled}
            showValidation={showValidation}
            isExpanded={expandedSections.drf}
            onExpandedChange={(expanded) =>
              setExpandedSections((prev) => ({ ...prev, drf: expanded }))
            }
          >
            <ErrorBoundary fallback={<ErrorFallback section="debt reserve fund (DRF)" />}>
              <DRFInfoInputs
                values={
                  drfInputs || {
                    startingBalance: '',
                    minimumBalance: '',
                    maximumBalance: '',
                  }
                }
                onUpdate={handlers.handleDRFInputsChange}
                showValidation={showValidation}
                isDisabled={!isDRFEnabled}
              />
            </ErrorBoundary>
          </SectionCard>

          {/* Bond Interest Rates Section */}
          <SectionCard
            title="Bond Interest Rates"
            description="Interest rate settings for bond escalation"
            status={bondInterestRates ? 'complete' : 'empty'}
            isRequired={false}
            isDisabled={!isBondInterestRatesEnabled}
            showValidation={showValidation}
            isExpanded={bondInterestRatesExpanded}
            onExpandedChange={setBondInterestRatesExpanded}
          >
            <ErrorBoundary fallback={<ErrorFallback section="bond interest rates" />}>
              <BondInterestRateInputs
                values={bondInterestRates}
                onChange={(field, value) => {
                  const numValue = parseFloat(value);
                  if (Number.isNaN(numValue)) {
                    setBondInterestRates((prev) => ({
                      ...prev,
                      [field]: value as unknown as number,
                    }));
                    return;
                  }
                  setBondInterestRates((prev) => ({
                    ...prev,
                    [field]: numValue,
                  }));
                }}
              />
            </ErrorBoundary>
          </SectionCard>
        </VStack>
      </PageLayoutGrid>
    </SidebarProvider>
  );
};

export default ProjectSetupPage;
